import { useState, useMemo, useEffect, useCallback } from 'react';
import { coderApi, type ExecutionJob, type ActiveSprite, type FlyMachine } from '../../api/coder';
import { ExecutionsTab } from '../coder/ExecutionsTab';
import { SpritesTab } from '../coder/SpritesTab';
import { ExecutionDetailSidebar } from '../coder/ExecutionDetailSidebar';

const STATE_ORDER: Record<string, number> = {
  started: 0, stopped: 1, created: 2, replacing: 3, destroyed: 4,
};

const STATE_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  started:   { bg: 'bg-accent-400', text: 'text-accent-700', label: 'Started' },
  stopped:   { bg: 'bg-amber-400', text: 'text-amber-700', label: 'Stopped' },
  created:   { bg: 'bg-blue-300', text: 'text-blue-700', label: 'Created' },
  replacing: { bg: 'bg-coral-300', text: 'text-coral-700', label: 'Replacing' },
  destroyed: { bg: 'bg-neutral-300', text: 'text-neutral-600', label: 'Destroyed' },
};

interface Props {
  sprint: any;
}

export function MonitorSpritesTab({ sprint }: Props) {
  const [coderExecutions, setCoderExecutions] = useState<ExecutionJob[]>([]);
  const [coderMachines, setCoderMachines] = useState<FlyMachine[]>([]);
  const [coderSprites, setCoderSprites] = useState<ActiveSprite[]>([]);
  const [spritesLoading, setSpritesLoading] = useState(true);
  const [spritesError, setSpritesError] = useState<string | null>(null);
  const [selectedExecution, setSelectedExecution] = useState<ExecutionJob | null>(null);
  const [stateFilter, setStateFilter] = useState<string | null>(null);
  const [spritesSubTab, setSpritesSubTab] = useState<'executions' | 'sprites'>('executions');

  const loadCoderData = useCallback(async () => {
    const bid = sprint?.batchId;
    if (!bid) {
      setCoderExecutions([]);
      setCoderSprites([]);
      setCoderMachines([]);
      setSpritesLoading(false);
      return;
    }
    try {
      const [executionsRes, spritesRes, machinesRes] = await Promise.all([
        coderApi.listExecutions({ limit: 50 }),
        coderApi.listSprites(),
        coderApi.listMachines().catch(() => ({ machines: [], count: 0, app: '', dashboardUrl: '' })),
      ]);
      const matchesBatch = (wfId: string) => wfId === bid || wfId.startsWith(bid + '-');
      setCoderExecutions(executionsRes.executions.filter((e: ExecutionJob) => e.batchId === bid));
      setCoderSprites(spritesRes.sprites.filter((s: ActiveSprite) => matchesBatch(s.workflowId)));
      setCoderMachines(machinesRes.machines.filter((m: FlyMachine) => matchesBatch(m.workflowId)));
      setSpritesError(null);
    } catch (err) {
      setSpritesError(err instanceof Error ? err.message : 'Failed to load sprite data');
    } finally {
      setSpritesLoading(false);
    }
  }, [sprint?.batchId]);

  useEffect(() => {
    loadCoderData();
    if (!sprint?.batchId) return;
    const interval = setInterval(loadCoderData, 5000);
    return () => clearInterval(interval);
  }, [loadCoderData, sprint?.batchId]);

  const stateCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const m of coderMachines) counts[m.state] = (counts[m.state] || 0) + 1;
    return counts;
  }, [coderMachines]);

  const handleViewExecution = async (batchId: string) => {
    try {
      const exec = await coderApi.getExecution(batchId);
      setSelectedExecution(exec);
    } catch (err) {
      setSpritesError(err instanceof Error ? err.message : 'Failed to load execution');
    }
  };

  const handleCancelExecution = async (batchId: string) => {
    try {
      await coderApi.cancelExecution(batchId);
      loadCoderData();
    } catch (err) {
      setSpritesError(err instanceof Error ? err.message : 'Failed to cancel execution');
    }
  };

  if (!sprint?.batchId) {
    return (
      <div className="text-center py-12 text-neutral-500">
        <p className="text-sm">No active build — sprite data will appear during builds.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Fleet overview bar */}
      {coderMachines.length > 0 && (
        <div className="space-y-1.5">
          <div className="flex h-2 rounded-full overflow-hidden bg-neutral-100">
            {Object.entries(stateCounts)
              .sort((a, b) => (STATE_ORDER[a[0]] ?? 99) - (STATE_ORDER[b[0]] ?? 99))
              .map(([state, count]) => {
                const pct = (count / coderMachines.length) * 100;
                const cfg = STATE_COLORS[state] || STATE_COLORS.destroyed;
                return (
                  <div key={state} className={`${cfg.bg} transition-all cursor-pointer hover:opacity-80`}
                    style={{ width: `${pct}%` }}
                    onClick={() => setStateFilter(stateFilter === state ? null : state)}
                    title={`${cfg.label}: ${count}`}
                  />
                );
              })}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <button onClick={() => setStateFilter(null)}
              className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded transition-colors ${
                stateFilter === null ? 'bg-coral-50 text-coral-700 ring-1 ring-coral-200' : 'bg-neutral-100 text-neutral-500 hover:bg-neutral-200'
              }`}>All ({coderMachines.length})</button>
            {Object.entries(stateCounts)
              .sort((a, b) => (STATE_ORDER[a[0]] ?? 99) - (STATE_ORDER[b[0]] ?? 99))
              .map(([state, count]) => {
                const cfg = STATE_COLORS[state] || STATE_COLORS.destroyed;
                return (
                  <button key={state} onClick={() => setStateFilter(stateFilter === state ? null : state)}
                    className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded transition-colors flex items-center gap-1 ${
                      stateFilter === state ? 'bg-coral-50 text-coral-700 ring-1 ring-coral-200' : 'bg-neutral-50 text-neutral-500 hover:bg-neutral-100'
                    }`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${cfg.bg}`} />
                    {cfg.label} ({count})
                  </button>
                );
              })}
          </div>
        </div>
      )}

      {/* Sub-tab toggle */}
      <div className="flex gap-1">
        <button
          onClick={() => setSpritesSubTab('executions')}
          className={`px-3 py-1.5 text-[11px] font-semibold rounded-md transition-colors ${
            spritesSubTab === 'executions'
              ? 'bg-coral-50 text-coral-700 ring-1 ring-coral-200'
              : 'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700'
          }`}
        >
          Executions ({coderExecutions.length})
        </button>
        <button
          onClick={() => setSpritesSubTab('sprites')}
          className={`px-3 py-1.5 text-[11px] font-semibold rounded-md transition-colors ${
            spritesSubTab === 'sprites'
              ? 'bg-coral-50 text-coral-700 ring-1 ring-coral-200'
              : 'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700'
          }`}
        >
          Sprites ({coderSprites.length})
        </button>
      </div>

      {spritesSubTab === 'executions' && (
        <ExecutionsTab
          executions={coderExecutions}
          onViewExecution={handleViewExecution}
          onCancelExecution={handleCancelExecution}
        />
      )}

      {spritesSubTab === 'sprites' && (
        <SpritesTab sprites={coderSprites} />
      )}

      {selectedExecution && (
        <ExecutionDetailSidebar
          execution={selectedExecution}
          onClose={() => setSelectedExecution(null)}
        />
      )}

      {spritesError && (
        <div className="text-[11px] text-red-500 bg-red-50 rounded-lg px-3 py-2">{spritesError}</div>
      )}
    </div>
  );
}
